import type { EnvironmentProviders, Provider } from '@angular/core';
import { inject, provideEnvironmentInitializer } from '@angular/core';
import type { FlipperConfiguration, FlipperProviderConfiguration } from './configuration';
import { FlipperException } from './flipper-exception';
import type { FlipperFeature } from '../models/flipper-provider';
import { flipperFeature, FlipperFeatureKind } from '../models/flipper-provider';
import { Flipper } from '../services/flipper';
import { BROWSER_DATA, FLIPPER_CONFIGURATION } from '../services/injection-tokens';
import { NavigationListener } from '../services/navigation-listener';
import { InMemoryStorage, LocalStorage, SessionStorage, Storage } from '../services/storage';
import detectPlatform from '../utils/browser.util';

const defaultConfig: Partial<FlipperConfiguration> = {
  basePath: 'api/v1/feature-management',
  httpSettings: {
    retryConfig: {
      count: 3,
      delay: 1000
    }
  },
  caching: {
    storageType: 'sessionstorage',
    ttl: 1000 * 60 * 5,
    featureFlagCacheKey: 'fl_f',
    userContextCacheKey: 'fl_uc'
  }
};

/**
 * Provides the Flipper feature service
 * @param {FlipperProviderConfiguration} config - The configuration for the Flipper feature service
 * @param {FlipperFeature[]} flipperFeatures - Array of feature flags to be managed
 * @returns {EnvironmentProviders} The providers for the Flipper feature service
 */
export function provideFlipper(
  config: FlipperProviderConfiguration,
  ...flipperFeatures: Array<FlipperFeature<FlipperFeatureKind>>
): Array<Provider | EnvironmentProviders> {
  const mergedConfig: FlipperProviderConfiguration = { ...defaultConfig, ...config };

  // Validate configuration
  if (!config.appId) {
    throw new FlipperException('applicationId is required');
  }

  const providers: Provider[] = [
    {
      provide: FLIPPER_CONFIGURATION,
      useValue: mergedConfig
    },
    {
      provide: BROWSER_DATA,
      useValue: {}
    },
    {
      provide: Flipper,
      useClass: Flipper
    }
  ];

  if (mergedConfig.caching) {
    const storageConfig = mergedConfig.caching;
    providers.push({
      provide: Storage,
      useFactory: () => {
        switch (storageConfig.storageType) {
          case 'sessionstorage':
            return new SessionStorage();
          case 'localstorage':
            return new LocalStorage();
          case 'inMemory':
            return new InMemoryStorage();
          default:
            throw new FlipperException('Invalid storageConfig storage type');
        }
      }
    });
  }

  // As well, add additional features if already provided (feature.providers)
  return [...providers, ...flipperFeatures.map(feature => feature.providers)];
}

/**
 * Automatically appends the browser context to the user context.
 * Includes Browser, Version and OS information.
 * @returns {FlipperFeature<FlipperFeatureKind.FINGERPRINTING>} The provider for the browser context service
 */
export function withBrowserContext(): FlipperFeature<FlipperFeatureKind.FINGERPRINTING> {
  return flipperFeature(FlipperFeatureKind.FINGERPRINTING, [
    {
      provide: BROWSER_DATA,
      useFactory: () => detectPlatform()
    }
  ]);
}

/**
 * Automatically listens to navigation events and evaluates features per navigation.
 * Uses the current user context to update the user context.
 * @returns {FlipperFeature<FlipperFeatureKind.NAVIGATION_LISTENER>} The provider for the navigation listener service
 */
export function withNavigationListener(): FlipperFeature<FlipperFeatureKind.NAVIGATION_LISTENER> {
  return flipperFeature(FlipperFeatureKind.NAVIGATION_LISTENER, [
    provideEnvironmentInitializer(() => {
      inject(NavigationListener).initialize();
    })
  ]);
}
